import { useEffect, useState } from 'react';

export type PreferredInputMode = 'midi' | 'audio';

export const INPUT_MODE_STORAGE_KEY = 'preferred-input-mode-v1';
export const EXPLICIT_INPUT_MODE_STORAGE_KEY = 'preferred-input-mode-explicit-v1';
export const PREFERRED_INPUT_MODE_CHANGED_EVENT = 'museflow_preferred_input_mode_changed';

const toInputMode = (value: string | null): PreferredInputMode | null => {
  if (value === 'audio') return 'audio';
  if (value === 'midi') return 'midi';
  return null;
};

export const readStoredPreferredInputMode = (): PreferredInputMode | null => {
  if (typeof window === 'undefined') return null;
  try {
    return toInputMode(window.localStorage.getItem(INPUT_MODE_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const readPreferredInputMode = (): PreferredInputMode => {
  return readStoredPreferredInputMode() ?? 'midi';
};

export const readExplicitPreferredInputMode = (): PreferredInputMode | null => {
  if (typeof window === 'undefined') return null;
  try {
    return toInputMode(window.localStorage.getItem(EXPLICIT_INPUT_MODE_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const writePreferredInputMode = (
  mode: PreferredInputMode,
  options: { explicit?: boolean } = {},
) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(INPUT_MODE_STORAGE_KEY, mode);
    if (options.explicit) {
      window.localStorage.setItem(EXPLICIT_INPUT_MODE_STORAGE_KEY, mode);
    }
  } catch {
    // Ignore storage failures.
  }

  window.dispatchEvent(
    new CustomEvent(PREFERRED_INPUT_MODE_CHANGED_EVENT, {
      detail: {
        mode,
        explicitMode: options.explicit ? mode : readExplicitPreferredInputMode(),
      },
    }),
  );
};

export const shouldShowSafariCompatibilityBlocker = (
  audioRecognitionEnabled: boolean,
  explicitPreferredInputMode: PreferredInputMode | null,
) => {
  return !audioRecognitionEnabled || explicitPreferredInputMode === 'midi';
};

export const useExplicitPreferredInputMode = (): PreferredInputMode | null => {
  const [explicitPreferredInputMode, setExplicitPreferredInputMode] =
    useState<PreferredInputMode | null>(() => readExplicitPreferredInputMode());

  useEffect(() => {
    const updateExplicitPreferredInputMode = () => {
      setExplicitPreferredInputMode(readExplicitPreferredInputMode());
    };

    window.addEventListener(PREFERRED_INPUT_MODE_CHANGED_EVENT, updateExplicitPreferredInputMode);
    window.addEventListener('storage', updateExplicitPreferredInputMode);

    return () => {
      window.removeEventListener(
        PREFERRED_INPUT_MODE_CHANGED_EVENT,
        updateExplicitPreferredInputMode,
      );
      window.removeEventListener('storage', updateExplicitPreferredInputMode);
    };
  }, []);

  return explicitPreferredInputMode;
};
